import { FOCUS_BORDER } from "../../config/focusStyles";
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import styled from "styled-components";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCMSImageURL, getOptimizedImageForType } from "../../utils/cms.utils";
import ContentTag from "../../utils/tag";
import classNames from "classnames";
import { getAgeRangePublicRoute } from "../../utils/ageRange.util";
import ViewMoreCard from "../common/ViewMoreCard";
import { FONT_FAMILY } from "../../config/globalStyles";
import { useInputMode } from "../../utils/hooks/useInputMode";
import OptimizedImage from "../common/OptimizedImage";

// ============================================================================
// CONSTANTS
// ============================================================================

const aspectGuide = {
  movie: "w-[250px] h-[350px]",
  searchbox: "w-[250px] h-[350px]",
  series: "w-[250px] h-[350px]",
  program: "h-[350px] w-[620px]",
  sport: "h-[350px] w-[620px]",
  music: "w-[250px] h-[250px]",
  animation: "w-[250px] h-[250px]",
  artist: "w-[215px] h-[215px]",
};

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

var ContentRowScrollingWrapper = styled.div`
  flex-shrink: 1;
  flex-grow: 1;
  padding-left: 60px;
  -webkit-transition: padding ease 0.2s;
  transition: padding ease 0.2s;
  overflow-x: auto;
`;

var ContentRowScrollingContent = styled.div`
  display: flex;
  -webkit-transition: all ease 0.5s;
  transition: all ease 0.5s;
`;

var ContentRowWrapper = styled.div`
  position: relative;
  min-height: 400px;
  background-color: ${({ $kind }) =>
    $kind === "music" ? "#121212" : "transparent"};
  border-radius: ${({ $kind }) => $kind === "music" && "20px"};
  width: ${({ $kind }) => ($kind === "music" ? "calc(100% - 5rem)" : "100%")};
  margin: ${({ $kind }) => ($kind === "music" ? "3rem auto" : "2rem 0")};
  &:after {
    content: "";
    position: absolute;
    width: 312px;
    height: 100%;
    background: ${({ $kind }) =>
    $kind !== "music" &&
    "linear-gradient(to right, #121212, rgba(0, 0, 0, 0))"};
    left: 0;
    top: 0;
  }
`;

var ContentRowTitle = styled.h3`
  color: white;
  margin-bottom: 22px;
  font-size: 27px;
  font-weight: 700;
  padding-right: 40px;
  padding-top: 40px;
  margin: 0;
  font-family: ${FONT_FAMILY};
`;

var ItemContainer = styled.div`
  position: relative;
  margin-left: 0.75rem;
`;

var ItemCard = styled.div`
  min-width: 215px;
  border-radius: 20px;
  background-size: cover;
  background-position: center;
  overflow: hidden;
  margin-bottom: 5%;
  position: relative;
  z-index: ${props => props.$focused ? 10 : 0};
  border: ${FOCUS_BORDER.inactive};
  margin: 2px;
  cursor: pointer;
`;

var FocusOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${props => props.$visible ? 1 : 0};
  border: ${props => props.$focused ? FOCUS_BORDER.active : FOCUS_BORDER.inactive};
  border-radius: 16px;
  pointer-events: none;
  -webkit-transition: opacity 0.2s;
  transition: opacity 0.2s;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  padding: 0 16px;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -ms-flex-direction: column;
  flex-direction: column;
  gap: 1rem;
  z-index: 10;
`;

var TitleText = styled.span`
  color: white;
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  font-family: ${FONT_FAMILY};
`;

var YearText = styled.span`
  color: white;
  font-size: 16px;
  font-weight: 400;
  text-align: center;
  font-family: ${FONT_FAMILY};
`;

var ArtistName = styled.p`
  color: white;
  font-weight: 700;
  margin-top: 0.25rem;
  margin-right: 0.25rem;
  font-family: ${FONT_FAMILY};
`;

var SeriesDecoration = styled.div`
  position: absolute;
  bottom: 0;
  top: -1rem;
  right: 5px;
  left: 5px;
  height: 100%;
`;

// ============================================================================
// COMPONENTS
// ============================================================================

const MovieItem = ({ item = {}, onFocus, shapeType }) => {
  const navigate = useNavigate();

  const {
    contentPhotos,
    title,
    year,
    slug,
    tags,
    image,
    farsiName,
    kind,
    contentType,
  } = item || {};

  const { focused, ref, focusSelf } = useFocusable({
    onFocus,
    onEnterPress: () => {
      navigate(
        shapeType === "artist"
          ? `/artists/${slug}`
          : `/${contentType?.kind || kind}/${slug}`
      );
    },
  });

  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  const getImageData = () => {
    if (shapeType === "artist") {
      return {
        src: getCMSImageURL(image, { width: 200, quality: 90 })
      };
    } else {
      return getOptimizedImageForType("verticalCover", contentPhotos, "TV", { width: 350, quality: 90, format: 'jpeg' });
    }
  };

  const imageData = getImageData();

  return (
    <ItemContainer>
      {kind === "series" && (
        <SeriesDecoration
          style={{
            backgroundImage: 'url("" )',
            backgroundSize: "100%",
            backgroundRepeat: "no-repeat",
          }}
        >
          <img src="/serial-pseudo.svg" alt="series" />
        </SeriesDecoration>
      )}
      <ItemCard
        ref={ref}
        $focused={focused}
        onClick={() =>
          navigate(
            shapeType === "artist"
              ? `/artists/${slug}`
              : `/${contentType?.kind || kind}/${slug}`
          )
        }
        onMouseEnter={handleMouseEnter}
        className={classNames("cursor-pointer", aspectGuide[shapeType])}
      >
        <OptimizedImage
          src={imageData.src}
          alt={title}
          className="w-full h-full object-cover"
          style={{ borderRadius: "inherit" }}
        />
        {tags?.[0] && <ContentTag label={tags?.[0]} />}
        <FocusOverlay $visible={focused} $focused={focused}>
          <TitleText>{title}</TitleText>
          <YearText>{year}</YearText>
        </FocusOverlay>
      </ItemCard>
      {farsiName && (
        <ArtistName>{farsiName}</ArtistName>
      )}
    </ItemContainer>
  );
};

export const MoviesSlider = ({
  movies,
  onFocus,
  rowTitle,
  shapeType = "movie",
  id,
  index,
  actionLink = null,
  cityKey = null,
  provinceKey = null,
}) => {
  const navigate = useNavigate();
  const ageRange = getAgeRangePublicRoute();
  const _items = movies.filter(
    (x) =>
      x.content?.ageRange <= ageRange ||
      x.contentMeta?.content?.ageRange <= ageRange ||
      x.ageRange <= ageRange
  );
  const { ref, focusKey } = useFocusable({
    onFocus,
    isFocusBoundary: true,
    focusBoundaryDirections: ["left"],
    focusKey: "movie_slider_" + id,
  });

  const [selected, setSelected] = useState(null);

  const scrollingRef = useRef(null);
  const innerRef = useRef(null);

  // Determine if View More should be shown - only when content length > 12
  const showViewMore = _items.length > 12;

  // Navigate to city content page
  const navigateToCityPage = () => {
    if (cityKey) {
      const params = new URLSearchParams();
      if (provinceKey) params.set("province", provinceKey);
      params.set("city", cityKey);
      navigate(`/anavatan/city?${params.toString()}`);
    }
  };

  const { isMouseFocus, isMouseFocusRef } = useInputMode();

  const onAssetFocus = useCallback(
    (props) => {
      if (isMouseFocusRef.current) return; // Skip horizontal scroll on mouse hover
      const { left } = props;
      if (left < 200) {
        innerRef.current.style.transform = `translateX(${shapeType === "sport" ? Math.abs(left - 500) : Math.abs(left - 300)
          }px)`;
      } else {
        innerRef.current.style.transform = `translateX(0px)`;
      }
    },
    [scrollingRef, isMouseFocusRef, shapeType]
  );

  const paddingOffsetCount = shapeType === "sport" ? 2 : 5;

  if (_items.length < 1) return null;
  return (
    <FocusContext.Provider value={focusKey}>
      <ContentRowWrapper ref={ref} $kind={shapeType} tabIndex={index}>
        <ContentRowTitle>{rowTitle}</ContentRowTitle>
        <ContentRowScrollingWrapper
          ref={scrollingRef}
          style={
            selected > paddingOffsetCount
              ? {
                paddingRight: 0,
              }
              : {
                paddingRight: 40,
              }
          }
        >
          <ContentRowScrollingContent ref={innerRef}>
            {_items?.map((item, itemIndex) => (
              <MovieItem
                item={item.content || item.artist}
                key={`ms_${item.content?.slug || item.artist?.slug
                  }_${id}_${itemIndex}`}
                onFocus={(e) => {
                  setSelected(itemIndex);
                  onAssetFocus(e);
                  // Also call parent onFocus for vertical scroll
                  if (onFocus) onFocus(e);
                }}
                shapeType={shapeType}
              />
            ))}
            {/* View More Card - shown at end when items > 12 */}
            {showViewMore && (
              <ViewMoreCard
                actionLink={actionLink}
                onMoreTap={cityKey ? navigateToCityPage : null}
                shapeType={shapeType}
                focusKey={`view_more_${id}`}
                onFocus={(e) => {
                  setSelected(_items.length);
                  onAssetFocus(e);
                  // Also call parent onFocus for vertical scroll
                  if (onFocus) onFocus(e);
                }}
              />
            )}
          </ContentRowScrollingContent>
        </ContentRowScrollingWrapper>
      </ContentRowWrapper>
    </FocusContext.Provider>
  );
};
